import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import Select from "@/components/base/Select";
import { useToast } from "@/components/base/Toast";
import { useStore } from "@/context/StoreContext";
import type { MenuProduct } from "@/types";
import CategoryManagerDrawer from "./components/CategoryManagerDrawer";
import ProductCard from "./components/ProductCard";

const ACTIVE_ORDER_STATUSES = ["new", "preparing", "almost", "ready"];

export default function MenuPage() {
  const navigate = useNavigate();
  const { show } = useToast();
  const {
    products: list,
    categories,
    orders,
    toggleProduct,
    deleteProduct,
  } = useStore();
  const [category, setCategory] = useState<string>("all");
  const [query, setQuery] = useState("");
  const [deleting, setDeleting] = useState<MenuProduct | null>(null);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  const categoryName = (id: string) =>
    categories.find((item) => item.id === id)?.name ?? "بدون تصنيف";

  const counts = useMemo(() => {
    const result: Record<string, number> = { all: list.length };
    list.forEach((product) => {
      result[product.categoryId] = (result[product.categoryId] ?? 0) + 1;
    });
    return result;
  }, [list]);

  const categoryOptions = useMemo(
    () => [
      { value: "all", label: `كل الأصناف (${counts.all ?? 0})` },
      ...categories.map((item) => ({
        value: item.id,
        label: `${item.name} (${counts[item.id] ?? 0})${
          item.isActive ? "" : " — معطّل"
        }`,
      })),
    ],
    [categories, counts],
  );

  const visible = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return list.filter((product) => {
      const matchCategory = category === "all" || product.categoryId === category;
      const matchQuery =
        keyword === "" || product.name.toLowerCase().includes(keyword);
      return matchCategory && matchQuery;
    });
  }, [list, category, query]);

  const handleToggle = (id: string, value: boolean) => {
    toggleProduct(id, value);
    show(value ? "تم تفعيل المنتج للطلب" : "تم إيقاف المنتج مؤقتًا");
  };

  const requestDelete = (product: MenuProduct) => {
    const blockingOrder = orders.find(
      (order) =>
        ACTIVE_ORDER_STATUSES.includes(order.status) &&
        order.lines.some((line) => line.name === product.name),
    );
    if (blockingOrder) {
      show(
        `لا يمكن حذف «${product.name}» — مرتبط بطلب نشط رقم #${blockingOrder.id}`,
        "error",
      );
      return;
    }
    setDeleting(product);
  };

  const confirmDelete = () => {
    if (!deleting) return;
    deleteProduct(deleting.id);
    show("تم حذف المنتج من المنيو", "warning");
    setDeleting(null);
  };

  return (
    <div className="space-y-4">
      <section className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-end">
          <div className="flex flex-col sm:flex-row gap-3">
            <Select
              value={category}
              options={categoryOptions}
              onChange={setCategory}
              icon="ri-apps-2-line"
              ariaLabel="تصفية حسب التصنيف"
              className="w-full sm:w-56"
            />
            <div className="flex items-center rounded-btn border border-background-200 bg-white px-3 focus-within:ring-2 focus-within:ring-primary-300">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line"></i>
              </span>
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="ابحث عن منتج"
                className="w-full sm:w-52 h-11 bg-transparent px-2 text-sm outline-none placeholder:text-foreground-300"
              />
            </div>
            <button
              type="button"
              onClick={() => setCategoriesOpen(true)}
              className="inline-flex items-center justify-center gap-2 h-11 rounded-btn bg-accent-100 px-5 text-sm font-semibold text-accent-900 hover:bg-accent-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-price-tag-3-line text-lg"></i>
              إدارة التصنيفات
            </button>
            <button
              type="button"
              onClick={() => navigate("/menu/new")}
              className="inline-flex items-center justify-center gap-2 h-11 rounded-btn bg-primary-500 px-5 text-sm font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-lg"></i>
              إضافة منتج
            </button>
          </div>
        </div>
      </section>

      {visible.length === 0 ? (
        <section className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card py-20 text-center">
          <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-3">
            <i className="ri-restaurant-2-line text-2xl"></i>
          </div>
          <p className="text-sm text-foreground-500">
            لا توجد منتجات مطابقة — جرّب تصنيفًا آخر أو أضف منتجًا جديدًا
          </p>
        </section>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          {visible.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              categoryName={categoryName(product.categoryId)}
              onToggle={handleToggle}
              onEdit={(item) => navigate(`/menu/${item.id}/edit`)}
              onDelete={requestDelete}
            />
          ))}
        </div>
      )}

      <CategoryManagerDrawer
        open={categoriesOpen}
        onClose={() => setCategoriesOpen(false)}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title="حذف المنتج"
        message={`هل أنت متأكد من حذف «${deleting?.name ?? ""}» من المنيو؟ لا يمكن التراجع عن هذه العملية.`}
        confirmLabel="حذف نهائيًا"
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}